import React, { useEffect, useState } from 'react';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { signInImpersonationUser } from '../../firebase/impersonationAuth';
import { Toast } from '../../components/Common/Toast';
import { setImpersonationSession } from '../../utils/impersonation.utils';

interface ImpersonationParams {
  accessToken: string;
  next: string;
}

const ImpersonateBridge: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const parseHashParams = (): ImpersonationParams | null => {
    const hash = window.location.hash;
    if (!hash || !hash.includes('#')) return null;

    try {
      const hashParts = hash.split('#');
      if (hashParts.length < 3) return null;

      const paramsString = hashParts[hashParts.length - 1];
      const params = new URLSearchParams(paramsString);

      const accessToken = params.get('accessToken');
      const next = params.get('next');

      if (!accessToken) {
        console.error('Missing accessToken in impersonation hash');
        return null;
      }

      return {
        accessToken: decodeURIComponent(accessToken),
        next: next ? decodeURIComponent(next) : '/',
      };
    } catch (err) {
      console.error('Error parsing impersonation hash:', err);
      return null;
    }
  };

  useEffect(() => {
    const processImpersonation = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const params = parseHashParams();
        if (!params) {
          setError('Invalid impersonation link. Missing required parameters.');
          return;
        }

        const userData = await signInImpersonationUser(params.accessToken);
        if (!userData?.user) {
          setError('Failed to authenticate impersonation user.');
          return;
        }

        const stored = setImpersonationSession({ token: userData.encryptedToken });
        if (!stored) {
          setError('Failed to store impersonation session.');
          return;
        }

        // Clear the URL hash so the token doesn't linger in history
        window.history.replaceState(null, '', window.location.pathname + window.location.search);

        Toast('Successfully signed in as impersonated user', false);

        // Hard reload at the target route. We need a full reload (not a
        // hash-only navigation) so firebase.ts re-initializes and
        // getAuthInstance() resolves to the impersonation auth — it reads
        // sessionStorage at module init time.
        window.location.href = `${window.location.origin}/#${params.next}`;
        window.location.reload();
      } catch (err: any) {
        console.error('Impersonation processing error:', err);
        setError(`Authentication failed: ${err?.message || 'unknown error'}`);
      } finally {
        setIsLoading(false);
      }
    };

    processImpersonation();
  }, []);

  const handleCloseTab = () => {
    window.close();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner />
          <div className="mt-4 space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">Signing you in as user...</h2>
            <p className="text-sm text-gray-500">Please wait while we authenticate you.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Impersonation Failed</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleCloseTab}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Close Tab
            </button>
            <p className="text-sm text-gray-500">You can close this tab and try again from the admin panel.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900">Redirecting...</h2>
        <p className="text-gray-600 mt-2">You should be redirected shortly.</p>
      </div>
    </div>
  );
};

export default ImpersonateBridge;
